import React from 'react';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { byPrefixAndName } from '@awesome.me/kit-4d9140695b/icons';
import { Paragraph, Label } from '../../typography';

export interface SelectOption {
  id: string;
  label: string;
  value: string;
  available: boolean;
  secondaryText?: string;
}

export interface SelectProps {
  options: SelectOption[];
  selectedOption?: SelectOption;
  onChange: (option: SelectOption) => void;
  label?: string;
  placeholder?: string;
  error?: boolean;
  disabled?: boolean;
  className?: string;
}

export const Select = ({
  options,
  selectedOption,
  onChange,
  label,
  placeholder = 'Select an option',
  error = false,
  disabled = false,
  className,
}: SelectProps) => {
  const hasSelection = !!selectedOption;

  return (
    <div className={className}>
      <Listbox value={selectedOption} onChange={onChange} disabled={disabled}>
        {label && <Label size="small">{label}</Label>}
        <div className="relative">
          <ListboxButton
            className={clsx(
              'relative w-full rounded-lg bg-[var(--color-primary-white)] py-3 pr-10 pl-4 text-left',
              'border transition-colors duration-200',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
              {
                'cursor-pointer': !disabled,
                'border-[var(--color-neutral-300)] hover:border-[var(--color-neutral-500)] focus-visible:ring-[var(--color-primary)]':
                  !error && !disabled,
                'border-[var(--color-red-600)] focus-visible:ring-[var(--color-red-600)]': error,
                'cursor-not-allowed opacity-50': disabled,
                'text-[var(--color-primary-black)]': hasSelection && !error,
                'text-[var(--color-neutral-500)]': !hasSelection && !error,
                'text-[var(--color-red-600)]': error,
              },
            )}
          >
            {({ open }) => (
              <>
                {hasSelection && selectedOption ? (
                  <div className="flex items-baseline gap-2">
                    <Paragraph size="small" color={error ? 'red-600' : 'primary-black'} className="block truncate">
                      {selectedOption.label}
                    </Paragraph>
                    {selectedOption.secondaryText && (
                      <Paragraph size="small" color="red-600" className="whitespace-nowrap">
                        {selectedOption.secondaryText}
                      </Paragraph>
                    )}
                  </div>
                ) : (
                  <Paragraph size="small" color={error ? 'red-600' : 'primary-black'} className="block truncate">
                    {placeholder}
                  </Paragraph>
                )}
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  {error ? (
                    <FontAwesomeIcon
                      icon={byPrefixAndName.fausb['circle-exclamation']}
                      className="h-5 w-5 text-[var(--color-red-600)]"
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={byPrefixAndName.fausb[open ? 'angle-up' : 'angle-down']}
                      className="h-5 w-5 text-[var(--color-neutral-500)]"
                    />
                  )}
                </span>
              </>
            )}
          </ListboxButton>

          <ListboxOptions
            transition
            className={clsx(
              'absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-[var(--color-primary-white)] py-1',
              'border border-[var(--color-neutral-300)] shadow-lg focus:outline-none',
              'transition duration-100 ease-in data-closed:opacity-0',
            )}
          >
            {options.map((option) => (
              <ListboxOption
                key={option.id}
                value={option}
                disabled={!option.available}
                className={clsx('group relative py-3 pr-10 pl-4 select-none', {
                  // Available options - normal styling with hover
                  'cursor-pointer text-[var(--color-primary-black)] data-focus:bg-[var(--color-primary-blurple)] data-focus:text-white data-focus:outline-hidden':
                    option.available,
                  // Sold out options - disabled styling, no hover effects
                  'cursor-not-allowed text-[var(--color-neutral-300)]': !option.available,
                })}
              >
                <div className="flex items-baseline gap-2">
                  <Paragraph
                    size="small"
                    color={option.available ? 'primary-black' : 'neutral-300'}
                    className={clsx('block truncate font-normal', {
                      'group-data-focus:text-white group-data-selected:font-semibold': option.available,
                    })}
                  >
                    {option.label}
                  </Paragraph>
                  {option.secondaryText && (
                    <Paragraph
                      size="small"
                      color={option.available ? 'red-600' : 'neutral-300'}
                      className={clsx('whitespace-nowrap', {
                        'group-data-focus:text-white': option.available,
                      })}
                    >
                      {option.secondaryText}
                    </Paragraph>
                  )}
                </div>

                {option.available && (
                  <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-[var(--color-primary-blurple)] group-not-data-selected:hidden group-data-focus:text-white">
                    <FontAwesomeIcon icon={byPrefixAndName.fausb.check} className="h-5 w-5" />
                  </span>
                )}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </div>
      </Listbox>
    </div>
  );
};
